import { SkyDialog, SkyText } from "@skypointcloud/skypoint-ui";
import type { SchemaPolicyMode } from "@shared/types/schemaChanges";
import type { ReportDefinitionValue } from "../EntityTable";
import { byCodeUnit } from "@shared/sort";
import { jsonbCanonical } from "@shared/sameJsonb";

/**
 * #2428: the two questions the Reports list asks before it changes what lands.
 *
 * - Remove: the report leaves the connection and its imports when the connection is saved; a
 *   table it loaded stays in the warehouse with its rows (skyData removes nothing), and a re-add
 *   rebuilds that table. A report never imported has no table, and the question says so.
 * - Rebuild: an edit of a report that has loaded is rebuilt whole on its next import (the runner
 *   always does it, R2), from the start date or the history window. The table is replaced, so
 *   rows older than that are removed from it and not loaded again (and a new definition that
 *   returns nothing empties it), a removed dimension's column stays and is left empty, and the
 *   connection's schema change policy decides what happens to a new column (R5).
 */

export const REMOVE_REPORT_TITLE = "Remove report?";

/** The saved report the Remove question is about, and whether its table has loaded. */
export interface RemoveReportTarget {
  name: string;
  table: string;
  landed: boolean;
}

/** Said on both: the removal is part of the connection's unsaved changes. */
export const REMOVE_REPORT_ON_SAVE = "The report is removed when you save the connection.";

export function removeReportSentence(target: RemoveReportTarget): string {
  const what = target.landed
    ? `skyData stops loading it. The table ${target.table} stays in your warehouse.`
    : "It has not been imported yet, so no table holds its data.";
  return `Remove report ${target.name}? ${what} ${REMOVE_REPORT_ON_SAVE}`;
}

export function RemoveReportDialog({
  target,
  onConfirm,
  onClose,
}: {
  target: RemoveReportTarget | null;
  onConfirm: (name: string) => void;
  onClose: () => void;
}) {
  return (
    <SkyDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="small"
      testId="remove-report-dialog"
      title={REMOVE_REPORT_TITLE}
      description={target ? removeReportSentence(target) : undefined}
      primaryAction={{
        label: "Remove report",
        destructive: true,
        onClick: () => {
          if (target) onConfirm(target.name);
          onClose();
        },
      }}
      secondaryAction={{ label: "Keep report", onClick: onClose }}
    />
  );
}

/** The schema change policy's one line, as a rebuild meets it (R5). */
export const SCHEMA_POLICY_REBUILD_LINES: Record<SchemaPolicyMode, string> = {
  allow_all:
    "This connection allows all column changes. A new dimension or metric is added as a column.",
  allow_columns:
    "This connection allows new columns only. A new dimension or metric is added as a column.",
  review:
    "This connection reviews every column change. New dimensions and metrics wait in Schema changes until someone approves them.",
};

/** One edited report the rebuild question names. */
export interface LandedReportEdit {
  name: string;
  /** Only the order of the dimensions or metrics moved: the columns' order changes. */
  reorderOnly: boolean;
}

/** The definition fields a write compares (jsonb's notion of equal: key order does not count). */
function comparable(definition: ReportDefinitionValue): string {
  return jsonbCanonical({
    propertyId: definition.propertyId,
    dimensions: definition.dimensions,
    metrics: definition.metrics,
    dimensionFilter: definition.dimensionFilter ?? null,
  });
}

function sortedLists(definition: ReportDefinitionValue): string {
  return comparable({
    ...definition,
    dimensions: [...definition.dimensions].sort(byCodeUnit),
    metrics: [...definition.metrics].sort(byCodeUnit),
  });
}

/**
 * The landed reports a draft changes, as the server will count them: a filter whose keys only
 * moved is no change (R13), while a reordered list is, because it is the column order.
 */
export function landedReportEdits(
  edits: ReadonlyArray<{
    name: string;
    was: ReportDefinitionValue;
    now: ReportDefinitionValue;
    landed: boolean;
  }>,
): LandedReportEdit[] {
  return edits
    .filter((edit) => edit.landed && comparable(edit.was) !== comparable(edit.now))
    .map((edit) => ({
      name: edit.name,
      reorderOnly: sortedLists(edit.was) === sortedLists(edit.now),
    }));
}

/** Where a rebuild reads from: the start date when one is set, else the history window. */
export function rebuildStartWords(startDate: string, historyDays: number): string {
  return startDate.trim()
    ? `from the start date, ${startDate.trim()}`
    : `from ${historyDays.toLocaleString()} days ago, the history window`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How many days a Full refresh reads: from the start date to yesterday when one is set (it
 * replaces the window for every import), else the history window. A date the form would refuse,
 * or one not in the past, reads as the window. `today` is for tests.
 */
export function fullRefreshDays(
  startDate: string,
  historyDays: number,
  today = new Date(),
): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate.trim());
  if (!match) return historyDays;
  const start = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const midnight = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((midnight - start) / DAY_MS);
  return Number.isFinite(days) && days > 0 ? days : historyDays;
}

export function rebuildSentences(input: {
  edits: readonly LandedReportEdit[];
  startDate: string;
  historyDays: number;
  policy: SchemaPolicyMode;
}): string[] {
  const names = input.edits.map((edit) => edit.name).join(", ");
  const lines = [
    `These reports have already loaded data: ${names}. Saving replaces each one's table on its next run, read again ${rebuildStartWords(input.startDate, input.historyDays)}. Rows older than that are removed from the table and are not loaded again.`,
    "A column for a dimension or metric you removed stays in the table and is left empty.",
    SCHEMA_POLICY_REBUILD_LINES[input.policy],
  ];
  if (input.edits.some((edit) => edit.reorderOnly)) {
    lines.push("A new order of dimensions or metrics is a new column order, so it rebuilds too.");
  }
  return lines;
}

export function RebuildReportsDialog({
  open,
  lines,
  onConfirm,
  onClose,
}: {
  open: boolean;
  lines: readonly string[];
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <SkyDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="medium"
      testId="rebuild-reports-dialog"
      title="Rebuild these reports?"
      primaryAction={{
        label: "Save and rebuild",
        onClick: () => {
          onConfirm();
          onClose();
        },
      }}
      secondaryAction={{ label: "Keep editing", onClick: onClose }}
    >
      <div className="flex flex-col gap-2">
        {lines.map((line) => (
          <SkyText key={line} as="p" size={300} block>
            {line}
          </SkyText>
        ))}
      </div>
    </SkyDialog>
  );
}
